import { type PersonSportRankingResponse } from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { formatHeightToString } from '@on3/ui-lib/utils/vitals';
import { PlayerRankingItemStatus } from '@public/components/Db/Rankings/PlayerRankings/PlayerRankingsStatus';
import { SportIcon } from '@public/components/Icons/SportIcon';
import { StarRatings } from '@public/components/UI/StarRatings/StarRatings';

import styles from './PlayerRanking.module.scss';

interface PlayerRankingProps extends PersonSportRankingResponse {
  useStateRanking?: boolean;
}

export const PlayerRanking = ({
  useStateRanking,
  ...data
}: PlayerRankingProps) => {
  const { formatHref } = useDynamicRoute();
  const {
    person,
    ratings,
    positionAbbreviation,
    consensusStateRank,
    consensusOverallRank,
  } = data;
  const {
    slug,
    height,
    defaultAsset,
    weight,
    defaultSport,
    highSchool,
    homeTownName,
    name,
    classYear,
    status,
    predictions,
    recruitmentKey,
  } = person ?? {};

  const sportName = defaultSport?.name || '';
  const activeRating = ratings?.find((rating) => rating.type === 'Industry');
  const ratingsUrl = formatHref({
    path: `/rivals/rankings/industry-player/[sport]/[year]/`,
    params: {
      sport: slugify(sportName),
      year: String(classYear),
    },
  });
  const rank = useStateRanking ? consensusStateRank : consensusOverallRank;

  return (
    <div className={styles.block}>
      <div className={styles.playerContainer}>
        <span className={styles.rank}>{rank}</span>
        <Link className={styles.avatarContainer} href={`/rivals/${slug}/`}>
          <Avatar
            altText={`${name} Avatar`}
            className={styles.playerAvatar}
            domain={defaultAsset?.domain}
            fit="cover"
            height={50}
            source={defaultAsset?.source}
            width={50}
          />
        </Link>
        <div className={styles.info}>
          {name && (
            <div className={styles.player}>
              <Link className={styles.name} href={`/rivals/${slug}/`}>
                {name}
              </Link>
            </div>
          )}
          <div aria-label="Player Vitals" className={styles.vitals}>
            <SportIcon className={styles.sportIcon} sportName={sportName} />
            {!!positionAbbreviation && <span>{positionAbbreviation}</span>}
            {!!height && <span>{formatHeightToString(height)}</span>}
            {!!weight && <span>{weight}</span>}
          </div>
          <div className={styles.rating}>
            <Link href={ratingsUrl}>
              <StarRatings stars={activeRating?.stars || 0} />
              <span className="sr-only">View Industry Comparison</span>
            </Link>
            {activeRating && <span>{activeRating.rating?.toFixed(2)}</span>}
          </div>
        </div>
        <div className={styles.status}>
          <PlayerRankingItemStatus
            predictions={predictions}
            recruitmentKey={recruitmentKey}
            slug={slug}
            sport={slugify(sportName)}
            status={status}
            year={String(classYear)}
          />
        </div>
      </div>

      {(!!highSchool || !!homeTownName) && (
        <div className={styles.locationContainer}>
          {!!highSchool && (
            <Link
              className={styles.highSchoolContainer}
              href={`/high-school/${highSchool.urlSlug}/`}
            >
              <Avatar
                altText={`${highSchool.name} Avatar`}
                className={styles.highSchoolAvatar}
                domain={highSchool.defaultAsset?.domain}
                fit="contain"
                height={50}
                source={highSchool.defaultAsset?.source}
                teamAvatar
                width={50}
              />
              <span>{highSchool.name}</span>
            </Link>
          )}
          {homeTownName && (
            <span className={styles.hometown}>{homeTownName}</span>
          )}
        </div>
      )}
    </div>
  );
};
